import React from "react";
import { FaCheck, FaImage, FaLink, FaSave } from "react-icons/fa";

import ModalShell from "./ModalShell";
import {
  avatars,
  DEFAULT_AVATAR,
  isValidHttpUrl,
} from "../utils/judokaProfileUtils";

const inputClassName =
  "min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white";

function PhotoModal({
  currentPhoto,
  selectedAvatar,
  photoUrl,
  photoUrlError,
  onPhotoUrlChange,
  onAvatarPreview,
  onAvatarSubmit,
  onPhotoUrlSubmit,
  onClose,
  saving,
  t,
}) {
  const previewUrl = isValidHttpUrl(photoUrl)
    ? photoUrl.trim()
    : selectedAvatar || currentPhoto;

  return (
    <ModalShell
      title={t("judokaProfile.selectAvatar", {
        defaultValue: "Changer la photo",
      })}
      eyebrow="Avatar ou photo en ligne"
      onClose={onClose}
    >
      <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Aperçu
          </p>

          <div className="mt-4 flex justify-center">
            <div className="relative h-40 w-40 overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
              <img
                src={previewUrl || DEFAULT_AVATAR}
                alt="Aperçu du judoka"
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
            </div>
          </div>

          <p className="mt-4 text-center text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Sélectionnez un avatar puis validez, ou collez l’URL d’une photo
            hébergée en ligne.
          </p>

          {selectedAvatar && !isValidHttpUrl(photoUrl) && (
            <button
              type="button"
              onClick={onAvatarSubmit}
              disabled={saving}
              className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Mise à jour..." : "Utiliser cet avatar"}
            </button>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                Avatars proposés
              </h3>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-white/50">
                {avatars.length} choix
              </span>
            </div>

            <div className="grid max-h-72 grid-cols-4 gap-3 overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.03] sm:grid-cols-5">
              {avatars.map((avatar, index) => {
                const isSelected = selectedAvatar === avatar;

                return (
                  <button
                    key={avatar}
                    type="button"
                    onClick={() => onAvatarPreview(avatar)}
                    disabled={saving}
                    className={`relative overflow-hidden rounded-2xl border-2 bg-slate-50 p-1 transition hover:-translate-y-0.5 dark:bg-white/[0.05] ${
                      isSelected
                        ? "border-orange-400 shadow-lg shadow-orange-500/20"
                        : "border-transparent hover:border-sky-300"
                    }`}
                    aria-label={`Choisir avatar ${index + 1}`}
                  >
                    <img
                      src={avatar}
                      alt={`Avatar ${index + 1}`}
                      className="aspect-square w-full rounded-xl object-cover"
                    />

                    {isSelected && (
                      <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg">
                        <FaCheck className="text-xs" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <form
            onSubmit={onPhotoUrlSubmit}
            className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]"
          >
            <label
              htmlFor="photo_url"
              className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-white/80"
            >
              <FaLink className="text-sky-600 dark:text-sky-300" />
              URL d’une photo en ligne
            </label>

            <input
              type="url"
              id="photo_url"
              name="photo_url"
              value={photoUrl}
              onChange={(event) => onPhotoUrlChange(event.target.value)}
              placeholder="https://exemple.com/photo.jpg"
              className={inputClassName}
            />

            {photoUrlError && (
              <p className="mt-2 text-sm font-semibold text-red-600 dark:text-red-300">
                {photoUrlError}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
            >
              <FaImage />
              {saving ? "Mise à jour..." : "Utiliser cette photo"}
            </button>
          </form>
        </div>
      </div>
    </ModalShell>
  );
}

export default PhotoModal;